/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
// NB The ticket that is sent back from a successful mutation does not match the data sent in
//    fields that are undefined will show up with null values in the server response for eg
//    this Merger takes those differences into account, it does not do a straight-up comparison

import { ExtendTicket, TicketExtended, TicketLineItemExtended, TicketLocationExtended } from "./TypeExtensions";
import { Signable, Ticket, TicketPax } from "../sharedCode/avroGraphql";
import { compareLists, lineItemMatches, locationMatches, paxMatches, signaturesMatch } from "./TicketComparer";

function isList(value: any): boolean {
    return hasValue(value) && typeof value === "object";
}

const hasValue = (value: any) => {
    return value != null && value != undefined;
};

/** Local-only extended fields on line items (server never returns these); preserve from local and use server id. */
function mergeLineItemWithServerId(serverItem: TicketLineItemExtended, localItem: TicketLineItemExtended | undefined): TicketLineItemExtended {
    if (!localItem) return serverItem;
    return { ...serverItem, inputValue: localItem.inputValue ?? serverItem.inputValue };
}

export const mergeLineItems = (localList: Record<string, unknown>, serverList: Record<string, unknown>): Array<TicketLineItemExtended> => {
    const localOnly = [] as Array<TicketLineItemExtended>;
    const local = localList as unknown as Array<TicketLineItemExtended>;
    const server = serverList as unknown as Array<TicketLineItemExtended>;

    for (let i = 0; i < local.length; i++) {
        const target = local[i];
        const serverMatch = server.find((l) => lineItemMatches(l, target));
        if (!serverMatch) {
            localOnly.push(target);
        }
    }
    // Server items first (with server ids); merge local-only extended fields from matching local item
    const mergedServer = server.map((s) => {
        const localMatch = local.find((l) => lineItemMatches(s, l));
        return mergeLineItemWithServerId(s, localMatch);
    });
    return [...mergedServer, ...localOnly];
};

/** Local-only extended fields on locations (server never returns these); preserve from local and use server id. */
function mergeLocationWithServerId(serverItem: TicketLocationExtended, localItem: TicketLocationExtended | undefined): TicketLocationExtended {
    if (!localItem) return serverItem;
    return { ...serverItem, inputHours: localItem.inputHours ?? serverItem.inputHours, keyHash: localItem.keyHash ?? serverItem.keyHash };
}

export const mergeLocations = (localList: Record<string, unknown>, serverList: Record<string, unknown>): Array<TicketLocationExtended> => {
    const localOnly = [] as Array<TicketLocationExtended>;
    const local = localList as unknown as Array<TicketLocationExtended>;
    const server = serverList as unknown as Array<TicketLocationExtended>;

    for (let i = 0; i < local.length; i++) {
        const target = local[i];
        if (server.findIndex((l) => locationMatches(l, target)) < 0) {
            localOnly.push(target);
        }
    }
    const mergedServer = server.map((s) => {
        const localMatch = local.find((l) => locationMatches(s, l));
        return mergeLocationWithServerId(s, localMatch);
    });
    return [...mergedServer, ...localOnly];
};

export const mergePax = (localList: Record<string, unknown>, serverList: Record<string, unknown>): Array<TicketPax> => {
    const localOnly = [] as Array<TicketPax>;
    const local = localList as unknown as Array<TicketPax>;
    const server = serverList as unknown as Array<TicketPax>;

    for (let i = 0; i < local.length; i++) {
        const target = local[i];
        if (server.findIndex((l) => paxMatches(l, target)) < 0) {
            localOnly.push(target);
        }
    }
    // Server items (with server ids) first; pax has no local-only extended fields
    return [...server, ...localOnly];
};

export const mergeSignatures = (localList: Record<string, unknown>, serverList: Record<string, unknown>): Array<Signable> => {
    const localOnly = [] as Array<Signable>;
    const local = localList as unknown as Array<Signable>;
    const server = serverList as unknown as Array<Signable>;

    for (let i = 0; i < local.length; i++) {
        const target = local[i];
        if (server.findIndex((l) => signaturesMatch(l, target)) < 0) {
            localOnly.push(target);
        }
    }
    // Server items (with server ids) first; signatures have no local-only extended fields
    return [...server, ...localOnly];
};

export function mergeLists(fieldName: string, localValue: Record<string, unknown>, serverValue: Record<string, unknown>): unknown[] {
    let listResult: unknown[] = [];
    switch (fieldName) {
        case "LineItems":
            listResult = mergeLineItems(localValue as Record<string, unknown>, serverValue as Record<string, unknown>);
            break;
        case "Locations":
            listResult = mergeLocations(localValue as Record<string, unknown>, serverValue as Record<string, unknown>);
            break;
        case "Pax":
            listResult = mergePax(localValue as Record<string, unknown>, serverValue as Record<string, unknown>);
            break;
        case "Signatures":
            listResult = mergeSignatures(localValue as Record<string, unknown>, serverValue as Record<string, unknown>);
            break;
        default:
        // do nothing
    }
    return listResult;
}

// NB - we have not seen any partially synced entities, just missing and incomplete sub lists so that is all this merger will currently resolve
export const mergeLocalTicketWithServerResult = (localTicket: TicketExtended, serverTicket: Ticket): TicketExtended => {
    const result = ExtendTicket(serverTicket);
    result.nextLocalId = localTicket.nextLocalId;

    for (const fieldName in localTicket) {
        const localList = (localTicket as Record<string, unknown>)[fieldName];
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        if (isList(localList)) {
            const serverList = serverTicket !== undefined && fieldName in serverTicket ? (serverTicket as Record<string, unknown>)[fieldName] : undefined;

            const listDifferences = compareLists(fieldName, localList as Record<string, unknown>, serverList as Record<string, unknown>);

            if (Object.keys(listDifferences).length > 0) {
                const newList = mergeLists(fieldName, localList as Record<string, unknown>, serverList as Record<string, unknown>);
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                result[fieldName] = newList;
                result.hasLocalChanges = true;
            }
        }
    }

    return result;
};
